import { useMutation } from "@tanstack/react-query";
import { getAuthApiClientConfig } from "@shared/apis/sidekick/api-factories";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { toast } from "@shared/services/toast";
import { appLogger } from "@shared/services/app-logger";

type Props = {
  onSuccess?: () => void;
  onError?: (error: any) => void;
};

type Params = {
  oldPassword: string;
  newPassword: string;
};

export const useChangePassword = (props?: Props) => {
  const { onSuccess, onError } = props || {};
  const config = getAuthApiClientConfig();

  const mutation = useMutation({
    mutationFn: async (params: Params) => {
      // Using the auth API client config to get the base path
      const url = `${config.basePath}/auth/change-password`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          oldPassword: params.oldPassword,
          newPassword: params.newPassword,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        throw await response.json();
      }

      onSuccess?.();
      toast.success("Password changed successfully");
      return await response.json();
    },
    onError: async (error) => {
      const apiError = await apiResponseErrorHandler(error);
      appLogger.error("Failed to change password", { error, apiError });
      onError?.(apiError);

      if (apiError.status === 401) {
        toast.error("Current password is incorrect");
        return;
      }

      if (apiError.status === 400) {
        toast.error("New password does not meet requirements");
        return;
      }

      toast.error(apiError.errors?.[0] || "Failed to change password");
    },
  });

  return mutation;
};
